import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Activity } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const PRIVACY_NOTICE_VERSION = '2026-08-13-v1.0';
const TERMS_OF_USE_VERSION = '2026-08-13-v1.0';

export default function Register() {
  const [form, setForm] = useState({
    email: '', password: '', name: '', organization: '',
    nif: '', phone: '', address: '',
  });
  const [privacyRead, setPrivacyRead] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({
        ...form, role: 'empresa',
        privacy_notice_acknowledged: privacyRead,
        privacy_notice_version: PRIVACY_NOTICE_VERSION,
        terms_accepted: termsAccepted,
        terms_version: TERMS_OF_USE_VERSION,
      });
      toast.success('Registro completado. Ya puedes iniciar sesión.');
      navigate('/login');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  return (
    <>
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <div className="mb-6 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg aluminum-bg flex items-center justify-center">
              <Activity className="w-6 h-6 text-aea-navy" strokeWidth={2.5} />
            </div>
            <span className="font-outfit font-bold text-xl">AEA · Espacio de Datos</span>
          </Link>
        </div>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-8">
            <h1 className="font-outfit font-bold text-2xl mb-1">Solicitar alta como empresa asociada</h1>
            <p className="text-slate-500 text-sm mb-6">Complete los datos y firme el contrato de adhesión posteriormente. Su alta pasará a validación por el equipo AEA.</p>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Razón social *</Label><Input required value={form.organization} onChange={on('organization')} data-testid="reg-org-input" /></div>
              <div><Label>NIF/CIF *</Label><Input required value={form.nif} onChange={on('nif')} data-testid="reg-nif-input" /></div>
              <div><Label>Persona de contacto *</Label><Input required value={form.name} onChange={on('name')} data-testid="reg-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={on('email')} data-testid="reg-email-input" /></div>
              <div><Label>Contraseña *</Label><Input type="password" required minLength={8} value={form.password} onChange={on('password')} data-testid="reg-password-input" /></div>
              <div><Label>Teléfono</Label><Input value={form.phone} onChange={on('phone')} data-testid="reg-phone-input" /></div>
              <div className="md:col-span-2"><Label>Dirección</Label><Input value={form.address} onChange={on('address')} data-testid="reg-address-input" /></div>
              <div className="md:col-span-2 space-y-4 border-t border-slate-200 pt-5 text-sm text-slate-600">
                <label className="flex items-start gap-3">
                  <input required type="checkbox" checked={privacyRead} onChange={(e) => setPrivacyRead(e.target.checked)} className="mt-1 h-4 w-4 accent-aea-blue" data-testid="privacy-read-checkbox" />
                  <span>He leído la <Link to="/privacy" target="_blank" className="text-aea-blue font-medium hover:underline">Política de Privacidad</Link> y la información sobre el tratamiento de mis datos.</span>
                </label>
                <label className="flex items-start gap-3">
                  <input required type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mt-1 h-4 w-4 accent-aea-blue" data-testid="terms-accepted-checkbox" />
                  <span>Acepto las <Link to="/terms" target="_blank" className="text-aea-blue font-medium hover:underline">Condiciones de Uso</Link> del Espacio de Datos.</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <Button type="submit" className="w-full bg-aea-blue hover:bg-aea-blue-dk" disabled={loading} data-testid="reg-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Registrarme como empresa'}
                </Button>
              </div>
            </form>
            <p className="text-center text-sm text-slate-500 mt-4">
              ¿Ya tienes cuenta? <Link to="/login" className="text-aea-blue font-medium">Iniciar sesión</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
    <Footer />
    </>
  );
}
